import React, { useState } from 'react';
import { Form, Input, Button, Card, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import ApiService from '../service/api-service';
import type { LoginRequest } from '../types/auth';
import logo from '../assets/images.jpeg';

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onFinish = async (values: LoginRequest) => {
    try {
      setLoading(true);
      await ApiService.login(values);
      message.success('Login successful!');
      navigate('/new-echo');
    } catch {
      // Error is already handled by ApiService
    } finally {
      setLoading(false);
    }
  };

  const inputClassName = "[&_.ant-input]:rounded-[6px] [&_.ant-input]:border-[#d9d9d9] [&_.ant-input:hover]:border-[#4096ff] [&_.ant-input:focus]:border-[#4096ff] [&_.ant-input:focus-within]:border-[#4096ff] [&_.ant-input:focus]:shadow-[0_0_0_2px_rgba(5,145,255,0.1)] [&_.ant-input-focused]:border-[#4096ff] [&_.ant-input-focused]:shadow-[0_0_0_2px_rgba(5,145,255,0.1)]";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background tilted cards */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-4 top-1/4 w-72 h-72 bg-cyan-100/50 rounded-3xl transform -rotate-12 blur-sm"></div>
        <div className="absolute right-10 bottom-1/4 w-80 h-80 bg-cyan-50/60 rounded-3xl transform rotate-12 blur-sm"></div>
        <div className="absolute left-1/3 top-1/3 w-64 h-64 bg-cyan-200/40 rounded-3xl transform -rotate-45 blur-sm"></div>
      </div>

      {/* Main content */}
      <Card className="max-w-md w-full relative bg-white/80 backdrop-blur-sm shadow-xl rounded-2xl">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="Pinnacle Echo Logo" className="h-32 w-auto" />
          </div>
          <h2 className="text-2xl font-semibold text-cyan-900">Sign in to Pinnacle Echo</h2>
          <p className="mt-3 text-sm text-gray-600">
            Experience the next generation of feedback collection
          </p>
        </div>

        <Form
          name="login"
          onFinish={onFinish}
          layout="vertical"
          className={inputClassName}
        >
          <Form.Item
            label={<span className="text-gray-700">Email</span>}
            name="email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input 
              placeholder="Enter your email" 
              className="h-11 text-base"
            />
          </Form.Item>

          <Form.Item
            label={<span className="text-gray-700">Password</span>}
            name="password"
            rules={[{ required: true, message: 'Please input your password!' }]}
          >
            <Input.Password 
              placeholder="Enter your password" 
              className="h-11 text-base"
            />
          </Form.Item>

          <Form.Item className="mb-2">
            <Button
              type="primary"
              htmlType="submit"
              className="w-full h-11 text-base bg-cyan-600 hover:bg-cyan-500 shadow-md hover:shadow-lg transition-all duration-200"
              loading={loading}
            >
              Sign in
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Login;